import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Calendar, Clock, Check, Users, Phone, MessageCircle, ChevronRight, X, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';

const TOUR_PACKAGES = [
  {
    id: 'cambodia-angkor-2026',
    title: 'Cambodia Discovery Trip',
    subtitle: 'Angkor Adventure',
    country: 'Cambodia',
    duration: '4 Days 3 Nights',
    dates: '28th - 31st Aug, 2026',
    pricing: [
      { currency: 'INR', amount: 38000, symbol: 'Rs.', label: 'Indian Rupees' },
      { currency: 'MYR', amount: 1900, symbol: 'RM', label: 'Malaysian Ringgit' },
      { currency: 'AUD', amount: 650, symbol: 'AUD', label: 'Australian Dollar' },
      { currency: 'SGD', amount: 575, symbol: 'SGD', label: 'Singapore Dollar' },
    ],
    heroImage: 'https://images.unsplash.com/photo-1653959864991-c828b72c82a8?w=1200&q=80',
    galleryImages: [
      'https://images.unsplash.com/photo-1675564813497-75cf769cb74b?w=600&q=80',
      'https://images.unsplash.com/photo-1551155674-6d39c2140d6d?w=600&q=80',
      'https://images.unsplash.com/photo-1673450020886-bce362a5ee24?w=600&q=80',
    ],
    highlights: [
      { title: 'Angkor Wat Temple City Tour', desc: 'Explore the world-famous ancient temple city.' },
      { title: 'Floating Village with Sunset', desc: 'Experience local life and a beautiful sunset on the water.' },
      { title: 'Kulen Mountain Waterfall', desc: 'Relax and enjoy the stunning natural waterfall. Explore 1000 Shiv lingas and Vishnu under the river water.' },
      { title: 'Banthey Srey Temple', desc: 'Visit the famous pink sandstone temple with detailed carvings.' },
    ],
    included: [
      'Accommodation & breakfast',
      'All temple and site seeing tickets',
      'Transportation including airport pickup',
      'Night market shopping with tuktuk',
    ],
  },
  {
    id: 'indonesia-bali-borobudur-2026',
    title: 'Bali & Borobudur Explorer',
    subtitle: 'Temples, Rice Terraces & Ancient Wonders',
    country: 'Indonesia',
    duration: '5 Days 4 Nights',
    dates: 'September, 2026',
    pricing: [
      { currency: 'MYR', amount: 2350, symbol: 'RM', label: 'Malaysian Ringgit' },
      { currency: 'AUD', amount: 850, symbol: 'AUD', label: 'Australian Dollar' },
      { currency: 'SGD', amount: 750, symbol: 'SGD', label: 'Singapore Dollar' },
      { currency: 'INR', amount: 54000, symbol: 'Rs.', label: 'Indian Rupees' },
    ],
    heroImage: 'https://images.unsplash.com/photo-1555865138-193ba536d7e0?w=1200&q=80',
    galleryImages: [
      'https://images.unsplash.com/photo-1620549146260-938c38c31c13?w=600&q=80',
      'https://images.unsplash.com/photo-1665825036585-488caec27c06?w=600&q=80',
      'https://images.unsplash.com/photo-1696220844406-d274f04c14f7?w=600&q=80',
    ],
    highlights: [
      { title: 'Explore Bali', desc: 'Visit Tanah Lot and Uluwatu Temples and Tegalalang Rice Terrace.' },
      { title: 'Visit Borobudur (Central Java)', desc: 'Explore the largest Buddhist temple in the world, featuring massive stupas and relief panels.' },
      { title: 'Visit Candi Sewu (Central Java)', desc: 'A vast 8th-century Buddhist complex located near Prambanan, surrounded by hundreds of smaller shrines.' },
      { title: 'Prambanan (Yogyakarta)', desc: 'A 9th-century complex, the largest Hindu temple in Indonesia, featuring high, pointed towers dedicated to Shiva, Vishnu, and Brahma.' },
    ],
    included: [
      'Accommodation & breakfast',
      'All temple and site seeing tickets',
      'Transportation including airport pickup',
      'Night market shopping with tuktuk',
    ],
  },
  {
    id: 'india-temple-pilgrimage-2026',
    title: 'Sacred Temples of South India',
    subtitle: 'Aaru Padai Veedu & Nava Graha Temples',
    country: 'India',
    duration: '11 Days 10 Nights',
    dates: 'November, 2026',
    pricing: [
      { currency: 'MYR', amount: 2500, symbol: 'RM', label: 'Malaysian Ringgit' },
      { currency: 'AUD', amount: 900, symbol: 'AUD', label: 'Australian Dollar' },
      { currency: 'SGD', amount: 800, symbol: 'SGD', label: 'Singapore Dollar' },
    ],
    heroImage: 'https://customer-assets.emergentagent.com/job_3a891074-22dc-494c-8d5b-bd85556ec29d/artifacts/7icnok4c_Thiruvannamalai.jpg',
    galleryImages: [
      'https://customer-assets.emergentagent.com/job_3a891074-22dc-494c-8d5b-bd85556ec29d/artifacts/xr8qjpmd_Aarupadai.jpg',
      'https://customer-assets.emergentagent.com/job_3a891074-22dc-494c-8d5b-bd85556ec29d/artifacts/2n25mm1u_Navagraha%20temple.jpg',
      'https://customer-assets.emergentagent.com/job_3a891074-22dc-494c-8d5b-bd85556ec29d/artifacts/e2ws8hcn_Thanjai%20temple.jpg',
    ],
    highlights: [
      { title: 'Six Prominent Temples of Murugan (Aaru Padai Veedu)', desc: 'Thiruthani, Swami Malai, Palamuthir Cholai, Thirupparankundram, Thiruchendur, Palani.' },
      { title: 'Nine Planets Representative Temples (Nava Graha Temples)', desc: 'Surya: Sooriyanar Kovil. Chandra: Kailasanathar Temple, Thingalur. Angarakan: Vaitheeswaran Kovil. Budhan: Thiruvenkadu. Guru: Alangudi. Sukran: Kanjanur. Shani: Tirunallar. Rahu: Thirunageswaram. Ketu: Keezhaperumpallam.' },
      { title: 'Special Temple Visits', desc: 'Thiruvannamalai, Thanjavur, Madurai, Trichy.' },
    ],
    included: [
      'Accommodation & breakfast',
      'All temple and site seeing tickets',
      'Transportation including airport pickup',
      'Night market shopping with tuktuk',
    ],
  },
];

/* ---------- Tour Card (Preview) ---------- */
const TourCard = ({ pkg, onViewDetails }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    className="group bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-white/30 overflow-hidden hover:shadow-2xl transition-shadow cursor-pointer"
    onClick={() => onViewDetails(pkg.id)}
    data-testid={`tour-card-${pkg.id}`}
  >
    <div className="relative h-52 overflow-hidden bg-ocean-light">
      <img src={pkg.heroImage} alt={pkg.title} className={`w-full h-full ${pkg.heroFit === 'contain' ? 'object-contain' : 'object-cover'} group-hover:scale-105 transition-transform duration-500`} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
      <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-md rounded-full px-3 py-1">
        <span className="text-xs font-semibold text-ocean">{pkg.duration}</span>
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-5">
        <p className="text-gold font-semibold tracking-wider uppercase text-xs mb-0.5">{pkg.country}</p>
        <h2 className="font-serif text-xl text-white">{pkg.title}</h2>
      </div>
    </div>

    <div className="p-5">
      <p className="text-ocean/70 text-sm italic mb-3">{pkg.subtitle}</p>

      <div className="flex items-center gap-3 mb-4">
        <div className="flex items-center gap-1.5 text-ocean/70">
          <Calendar className="w-3.5 h-3.5" />
          <span className="text-xs">{pkg.dates}</span>
        </div>
      </div>

      {/* Price list */}
      <div className="flex flex-wrap gap-2 mb-4">
        {pkg.pricing.map((p, i) => (
          <span key={`card-price-${pkg.id}-${p.currency}-${i}`}
            className="bg-ocean/8 text-ocean font-semibold text-xs px-2.5 py-1 rounded-full">
            {p.symbol} {p.amount.toLocaleString()}
          </span>
        ))}
      </div>

      <Button className="w-full bg-gradient-to-r from-ocean to-sky text-white rounded-full h-10 gap-2 font-semibold shadow-md hover:shadow-lg transition-all"
        data-testid={`view-details-${pkg.id}`}>
        View Details <ChevronRight className="w-4 h-4" />
      </Button>
    </div>
  </motion.div>
);

/* ---------- Tour Detail (Full View) ---------- */
const TourDetail = ({ pkg, onClose }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: 40 }}
    transition={{ duration: 0.4 }}
    className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/30 overflow-hidden"
    data-testid={`tour-detail-${pkg.id}`}
  >
    {/* Hero */}
    <div className="relative h-64 md:h-80 overflow-hidden bg-ocean-light">
      <img src={pkg.heroImage} alt={pkg.title} className={`w-full h-full ${pkg.heroFit === 'contain' ? 'object-contain' : 'object-cover'}`} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
      <button onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 bg-black/40 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-black/60 transition-colors"
        data-testid="close-detail-btn">
        <X className="w-5 h-5" />
      </button>
      <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
        <p className="text-gold font-semibold tracking-wider uppercase text-sm mb-1">{pkg.country}</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-1">{pkg.title}</h2>
        <p className="text-white/80 font-serif text-lg italic">{pkg.subtitle}</p>
      </div>
    </div>

    <div className="p-6 md:p-8">
      {/* Quick Info */}
      <div className="flex flex-wrap items-start gap-4 mb-6">
        <div className="flex items-center gap-2 bg-ocean/8 px-4 py-2 rounded-full">
          <Clock className="w-4 h-4 text-ocean" />
          <span className="text-sm font-medium text-ocean">{pkg.duration}</span>
        </div>
        <div className="flex items-center gap-2 bg-gold/10 px-4 py-2 rounded-full">
          <Calendar className="w-4 h-4 text-gold" />
          <span className="text-sm font-medium text-ocean">{pkg.dates}</span>
        </div>

        {/* Pricing */}
        <div className="bg-white/95 backdrop-blur-md rounded-xl shadow-lg border border-gold/30 px-5 py-3">
          <p className="text-xs text-ocean/60 font-medium mb-2">Price per person</p>
          <div className="space-y-1.5">
            {pkg.pricing.map((p, i) => (
              <div key={`detail-price-${pkg.id}-${p.currency}-${i}`} className="flex items-baseline gap-1.5">
                <span className="text-lg font-bold text-ocean">{p.symbol} {p.amount.toLocaleString()}</span>
                <span className="text-xs text-ocean/50">({p.label})</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Gallery */}
      <div className="grid grid-cols-3 gap-3 mb-8 rounded-xl overflow-hidden">
        {pkg.galleryImages.map((img, i) => (
          <div key={`gallery-${pkg.id}-${i}`} className="aspect-[4/3] overflow-hidden rounded-lg">
            <img src={img} alt={`${pkg.title} ${i + 1}`} className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />
          </div>
        ))}
      </div>

      {/* Trip Highlights */}
      <div className="mb-8">
        <h3 className="font-serif text-xl text-ocean mb-4 flex items-center gap-2">
          <MapPin className="w-5 h-5 text-gold" /> Trip Highlights
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          {pkg.highlights.map((h, i) => (
            <div key={`highlight-${pkg.id}-${i}`} className="flex gap-3 p-4 bg-gradient-to-br from-ocean/5 to-sky/5 rounded-xl border border-ocean/10">
              <div className="w-8 h-8 bg-gradient-to-br from-gold to-gold-light rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white font-bold text-sm">{i + 1}</span>
              </div>
              <div>
                <h4 className="font-semibold text-ocean text-sm">{h.title}</h4>
                <p className="text-ocean/60 text-xs mt-0.5">{h.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* What's Included */}
      <div className="mb-8">
        <h3 className="font-serif text-xl text-ocean mb-4">What's Included</h3>
        <div className="grid grid-cols-2 gap-3">
          {pkg.included.map((item, i) => (
            <div key={`included-${pkg.id}-${i}`} className="flex items-center gap-2.5 p-3 bg-green-50/80 rounded-xl border border-green-200/50">
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <Check className="w-3.5 h-3.5 text-white" />
              </div>
              <span className="text-sm text-ocean font-medium">{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="bg-gradient-to-r from-ocean/10 to-gold/10 rounded-xl p-5 border border-ocean/15 text-center">
        <p className="font-serif text-lg text-ocean mb-3">Ready to explore {pkg.country}?</p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <a href={`https://api.whatsapp.com/send?phone=61401050457&text=Hi%2C%20I%27m%20interested%20in%20the%20${encodeURIComponent(pkg.title)}`} target="_blank" rel="noopener noreferrer">
            <Button className="bg-green-600 hover:bg-green-700 text-white rounded-full px-6 h-11 gap-2 shadow-lg" data-testid={`book-whatsapp-${pkg.id}`}>
              <MessageCircle className="w-4 h-4" /> Book via WhatsApp
            </Button>
          </a>
          <a href="tel:+61401050457">
            <Button variant="outline" className="border-ocean text-ocean hover:bg-ocean hover:text-white rounded-full px-6 h-11 gap-2" data-testid={`book-phone-${pkg.id}`}>
              <Phone className="w-4 h-4" /> Call Us
            </Button>
          </a>
        </div>
      </div>
    </div>
  </motion.div>
);

/* ---------- Page ---------- */
export default function TourPackagesPage() {
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState(null);
  const selectedPkg = TOUR_PACKAGES.find(p => p.id === selectedId);

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="tour-packages-page">
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-ocean via-ocean-light/90 to-sky/70" />
        <div className="absolute top-20 right-10 w-96 h-96 bg-gold/15 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-40 left-20 w-80 h-80 bg-sky/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      <Header />
      <main className="pt-24 pb-16 px-6 md:px-12 lg:px-24 relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="mb-6">
            <button onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white text-sm flex items-center gap-1 transition-colors" data-testid="back-to-dashboard-top">
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </button>
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
            <div className="w-14 h-14 bg-gold/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <MapPin className="w-7 h-7 text-gold" />
            </div>
            <h1 className="font-serif text-3xl md:text-4xl text-white drop-shadow-lg mb-2">Tour Packages</h1>
            <p className="text-white/80">Curated adventures with everything included</p>
          </motion.div>

          <AnimatePresence mode="wait">
            {selectedPkg ? (
              /* Expanded Detail View */
              <motion.div key="detail" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <button onClick={() => setSelectedId(null)}
                  className="flex items-center gap-1.5 text-white/80 hover:text-white mb-4 text-sm font-medium transition-colors"
                  data-testid="back-to-packages">
                  <ChevronRight className="w-4 h-4 rotate-180" /> Back to all packages
                </button>
                <TourDetail pkg={selectedPkg} onClose={() => setSelectedId(null)} />
              </motion.div>
            ) : (
              /* Card Grid */
              <motion.div key="grid" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <div className="grid md:grid-cols-3 gap-6">
                  {TOUR_PACKAGES.map((pkg, i) => (
                    <motion.div key={pkg.id} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
                      <TourCard pkg={pkg} onViewDetails={setSelectedId} />
                    </motion.div>
                  ))}
                </div>

                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
                  className="mt-8 text-center">
                  <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                    <Users className="w-8 h-8 text-gold mx-auto mb-3" />
                    <p className="text-white font-serif text-lg mb-1">More Destinations Coming Soon</p>
                    <p className="text-white/60 text-sm">We're crafting exclusive packages for Vietnam, Thailand, Sri Lanka and more.</p>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex justify-center mt-8">
            <button onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white border border-white/30 hover:bg-white/10 rounded-full px-6 py-2 text-sm font-medium flex items-center gap-2 transition-all" data-testid="back-to-dashboard-bottom">
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
